import { useState, useCallback, useEffect, useRef } from 'react';
import { setLastKnownLocation } from '../utils/eventLog';
import { buildGeoErrorMessage, detectGeoPlatform, isOsLocationServicesOff } from '../utils/geoErrors';

export interface GeolocationPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export interface GeolocationError {
  code: number;
  message: string;
  /**
   * #2208 — true when the SITE permission is granted but the OS still denied
   * the fix (OS Location Services off for the browser). Lets the UI show
   * device-aware "turn on Location Services" guidance instead of a generic
   * "permission denied" that misleads reps who already granted access.
   */
  osLocationServicesOff?: boolean;
}

interface UseGeolocationResult {
  position: GeolocationPosition | null;
  error: GeolocationError | null;
  isLoading: boolean;
  isSupported: boolean;
  getCurrentPosition: () => Promise<GeolocationPosition | null>;
  /**
   * Collect GPS fixes over a short window and resolve with all of them (#2817).
   * A single one-shot fix can be a persistent Wi-Fi/network outlier (tens of km
   * off with confident accuracy); watching for a few seconds usually catches the
   * true on-site fix as the receiver refines. Early-stops via `shouldStop`.
   */
  sampleFixes: (opts?: {
    durationMs?: number;
    shouldStop?: (fix: GeolocationPosition) => boolean;
  }) => Promise<GeolocationPosition[]>;
  watchPosition: () => void;
  clearWatch: () => void;
  calculateDistance: (lat1: number, lng1: number, lat2: number, lng2: number) => number;
  isWithinRadius: (targetLat: number, targetLng: number, radiusMeters: number) => boolean;
}

export type AccuracyLevel = 'good' | 'weak' | 'very_weak' | 'unknown';

// 100m is the "verified on-site" boundary used by check-in flows (and #1779 manual entry).
// Above 1000m the fix is so coarse that distance comparisons become meaningless.
export function classifyAccuracy(accuracyMeters: number | null | undefined): AccuracyLevel {
  if (accuracyMeters == null) return 'unknown';
  if (accuracyMeters <= 100) return 'good';
  if (accuracyMeters <= 1000) return 'weak';
  return 'very_weak';
}

export type CheckInFixCode = 'verified' | 'off_site' | 'low_accuracy';

// Interactive check-in classification (#2817 / diag #62). Deliberately distinct
// from computeVerificationState (which gates submit-time data integrity): here we
// only force the scary "you appear to be X km away — provide a reason" override
// for a fix we actually trust. A coarse fix (accuracy circle wider than the
// geofence) that ALSO lands far is ambiguous — a single Wi-Fi/network fix can be
// tens of km off even when the rep is standing in the store (diag #62) — so it's
// 'low_accuracy' (retry / soft override), never a confident off_site. A coarse
// fix that still lands within the geofence is good enough to check in.
export function classifyCheckInFix(
  distanceMeters: number,
  accuracyMeters: number | null | undefined,
  radiusMeters = 200
): CheckInFixCode {
  if (distanceMeters <= radiusMeters) return 'verified';
  if (accuracyMeters != null && accuracyMeters > radiusMeters) return 'low_accuracy';
  return 'off_site';
}

export type VerificationCode =
  | 'verified'
  | 'off_site'
  | 'gps_low_accuracy'
  | 'gps_unavailable'
  | 'store_missing_coords';

export interface VerificationResult {
  code: VerificationCode;
  distance: number | null;
}

// Decides whether we can prove a rep was at the store at submit time.
// Priority: gps_unavailable > store_missing_coords > gps_low_accuracy > off_site > verified.
// `position` may be null if GPS was denied / timed out / unsupported.
// `storeLat` / `storeLng` may be 0 or null if the store record has no coords.
export function computeVerificationState(
  position: GeolocationPosition | null,
  storeLat: number | null | undefined,
  storeLng: number | null | undefined,
  radiusMeters = 200
): VerificationResult {
  if (!position) {
    return { code: 'gps_unavailable', distance: null };
  }
  const storeHasCoords = storeLat != null && storeLng != null && (storeLat !== 0 || storeLng !== 0);
  if (!storeHasCoords) {
    return { code: 'store_missing_coords', distance: null };
  }
  if (position.accuracy != null && position.accuracy > radiusMeters) {
    return { code: 'gps_low_accuracy', distance: null };
  }
  const distance = haversineDistance(position.latitude, position.longitude, storeLat as number, storeLng as number);
  if (distance > radiusMeters) {
    return { code: 'off_site', distance };
  }
  return { code: 'verified', distance };
}

// Haversine formula to calculate distance between two points in meters
function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function useGeolocation(): UseGeolocationResult {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<GeolocationError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [watchId, setWatchId] = useState<number | null>(null);

  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // #2208 — track the Permissions-API geolocation state so handleError can tell
  // "site permission denied" from "site granted but OS Location Services off".
  const permissionGrantedRef = useRef(false);
  useEffect(() => {
    const perms = typeof navigator !== 'undefined' ? navigator.permissions : undefined;
    if (!perms?.query) return;
    let status: PermissionStatus | null = null;
    perms
      .query({ name: 'geolocation' as PermissionName })
      .then((s) => {
        status = s;
        permissionGrantedRef.current = s.state === 'granted';
        s.onchange = () => {
          permissionGrantedRef.current = s.state === 'granted';
        };
      })
      .catch(() => {
        // Permissions API unavailable (older Safari) — leave default false.
      });
    return () => {
      if (status) status.onchange = null;
    };
  }, []);

  const handleSuccess = useCallback((pos: globalThis.GeolocationPosition) => {
    setPosition({
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      timestamp: pos.timestamp,
    });
    setError(null);
    setIsLoading(false);
  }, []);

  const handleError = useCallback((err: globalThis.GeolocationPositionError) => {
    // #2208 — device-aware message. PERMISSION_DENIED while the site permission
    // is granted means OS Location Services is off for the browser, not that the
    // rep refused — show how to fix it for their device.
    const ctx = { permissionGranted: permissionGrantedRef.current, platform: detectGeoPlatform() };
    setError({
      code: err.code,
      message: buildGeoErrorMessage(err.code, ctx),
      osLocationServicesOff: isOsLocationServicesOff(err.code, ctx),
    });
    setIsLoading(false);
  }, []);

  const getCurrentPosition = useCallback(async (): Promise<GeolocationPosition | null> => {
    if (!isSupported) {
      setError({ code: 0, message: 'Geolocation is not supported by this browser' });
      return null;
    }

    setIsLoading(true);
    setError(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const geoPos: GeolocationPosition = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            timestamp: pos.timestamp,
          };
          setPosition(geoPos);
          setError(null);
          setIsLoading(false);
          // Mirror into the event-log location cache so #1950 events
          // captured outside the check-in flow (task lifecycle, survey
          // submit, photo upload, etc.) automatically carry a rep
          // location for telemetry.
          setLastKnownLocation(geoPos.latitude, geoPos.longitude);
          resolve(geoPos);
        },
        (err) => {
          handleError(err);
          resolve(null);
        },
        {
          // Used for check-in / proximity decisions - must be fresh. #1818
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    });
  }, [isSupported, handleError]);

  const sampleFixes = useCallback(
    (opts?: {
      durationMs?: number;
      shouldStop?: (fix: GeolocationPosition) => boolean;
    }): Promise<GeolocationPosition[]> => {
      if (!isSupported) {
        setError({ code: 0, message: 'Geolocation is not supported by this browser' });
        return Promise.resolve([]);
      }
      const durationMs = opts?.durationMs ?? 10000;
      setIsLoading(true);
      setError(null);

      return new Promise((resolve) => {
        const fixes: GeolocationPosition[] = [];
        let settled = false;
        let id: number | null = null;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const finish = () => {
          if (settled) return;
          settled = true;
          if (id !== null) navigator.geolocation.clearWatch(id);
          if (timer) clearTimeout(timer);
          setIsLoading(false);
          if (fixes.length > 0) {
            const latest = fixes[fixes.length - 1];
            setPosition(latest);
            setLastKnownLocation(latest.latitude, latest.longitude);
          }
          resolve(fixes);
        };

        id = navigator.geolocation.watchPosition(
          (pos) => {
            const geoPos: GeolocationPosition = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              timestamp: pos.timestamp,
            };
            fixes.push(geoPos);
            // Early-stop the moment a caller-defined "good enough" fix arrives so
            // the happy path (rep is on-site) doesn't wait out the whole window.
            if (opts?.shouldStop?.(geoPos)) finish();
          },
          (err) => {
            // Only surface the error if we never got a single fix; otherwise keep
            // whatever we collected and let the window close normally.
            if (fixes.length === 0) {
              handleError(err);
              finish();
            }
          },
          { enableHighAccuracy: true, timeout: durationMs, maximumAge: 0 }
        );

        timer = setTimeout(finish, durationMs);
      });
    },
    [isSupported, handleError]
  );

  const watchPosition = useCallback(() => {
    if (!isSupported || watchId !== null) return;

    const id = navigator.geolocation.watchPosition(handleSuccess, handleError, {
      // Streaming feed for live distance display - no cache. #1818
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
    setWatchId(id);
  }, [isSupported, watchId, handleSuccess, handleError]);

  const clearWatch = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
  }, [watchId]);

  // Calculate distance between two coordinates
  const calculateDistance = useCallback(
    (lat1: number, lng1: number, lat2: number, lng2: number): number => {
      return haversineDistance(lat1, lng1, lat2, lng2);
    },
    []
  );

  // Check if current position is within radius of target
  const isWithinRadius = useCallback(
    (targetLat: number, targetLng: number, radiusMeters: number): boolean => {
      if (!position) return false;
      const distance = haversineDistance(
        position.latitude,
        position.longitude,
        targetLat,
        targetLng
      );
      return distance <= radiusMeters;
    },
    [position]
  );

  // Cleanup watch on unmount
  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  return {
    position,
    error,
    isLoading,
    isSupported,
    getCurrentPosition,
    sampleFixes,
    watchPosition,
    clearWatch,
    calculateDistance,
    isWithinRadius,
  };
}
